import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,

  CircularProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Add,
  FilterList,
  Download,
  Refresh,
  Clear
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import { UserRecord, SearchFilters } from '../types';
import { getUsers, deleteUser } from '../services/userService';
import { useApp, useErrorHandler } from '../contexts/AppContext';
import UserAvatar from '../components/UserAvatar';
import UserForm from '../components/UserForm';
import UserFilter from '../components/UserFilter';

const UserList: React.FC = () => {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [localSearchTerm, setLocalSearchTerm] = useState(''); // 입력 필드용 로컬 상태
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserRecord | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<SearchFilters>({
    search: '',
    headquarters: '',
    department: '',
    position: '',
    assignments: '',
    location: '',
    initialEducationStartDate: '',
    initialEducationEndDate: '',
    recentEducationStartDate: '',
    recentEducationEndDate: ''
  });

  const { canEdit, isAdmin } = useApp();
  const handleError = useErrorHandler();

  // 사용자 목록 로드 함수를 useCallback으로 메모이제이션
  const loadUsers = useCallback(async (searchValue?: string, filterValues?: SearchFilters) => {
    try {
      setLoading(true);
      
      const currentFilters: SearchFilters = {
        ...(filterValues || filters),
        search: searchValue !== undefined ? searchValue : searchTerm
      };

      const result = await getUsers(currentFilters, { field: 'createdAt', direction: 'desc' });
      setUsers(result.users);
    } catch (error: any) {
      handleError(error, '인원 목록 조회');
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm, handleError]);

  // 새로고침 핸들러
  const handleRefresh = useCallback(() => {
    loadUsers(searchTerm, filters);
  }, [loadUsers, searchTerm, filters]);

  // 검색 실행 핸들러
  const handleSearch = useCallback(() => {
    if (localSearchTerm.trim().length >= 2 || localSearchTerm.trim().length === 0) {
      setSearchTerm(localSearchTerm.trim());
    }
  }, [localSearchTerm]);

  // 엔터키 핸들러
  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // 검색어 초기화 핸들러
  const handleClearSearch = useCallback(() => {
    setLocalSearchTerm('');
    setSearchTerm('');
  }, []);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // 엔터키 전용 검색 - 자동 디바운스 제거
  // 빈 문자열일 때만 자동으로 초기화
  useEffect(() => {
    if (localSearchTerm === '' && searchTerm !== '') {
      setSearchTerm('');
    }
  }, [localSearchTerm, searchTerm]);

  // 검색어나 필터 변경 시 데이터 로드
  useEffect(() => {
    const combinedFilters = {
      ...filters,
      search: searchTerm
    };
    loadUsers(searchTerm, combinedFilters);
    if (page !== 0) {
      setPage(0);
    }
  }, [searchTerm, filters, loadUsers, page]);

  // 페이지 변경 시 재조회
  useEffect(() => {
    if (page > 0) {
      loadUsers();
    }
  }, [page]);

  // 페이지 변경
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // 페이지 크기 변경
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 사용자 삭제 확인 다이얼로그 열기
  const handleDeleteClick = (user: UserRecord) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // 사용자 삭제 실행
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setDeleteLoading(true);
      await deleteUser(userToDelete.id);
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      handleError(error, '인원 삭제');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 인원 추가/편집 폼 열기
  const handleOpenForm = (user?: UserRecord) => {
    setEditingUser(user || null);
    setFormOpen(true);
  };

  // 인원 추가/편집 성공 처리
  const handleFormSuccess = (user: UserRecord) => {
    console.log('사용자 목록 업데이트:', user);
    if (editingUser) {
      // 편집: 기존 사용자 업데이트 (파일 업로드/삭제 포함)
      setUsers(prev => {
        const updated = prev.map(u => u.id === user.id ? user : u);
        console.log('사용자 목록 업데이트 완료');
        return updated;
      });
      
      // editingUser도 최신 정보로 업데이트 (UserForm이 최신 데이터를 받도록)
      setEditingUser(user);
    } else {
      // 추가: 새 사용자 목록에 추가
      setUsers(prev => [user, ...prev]);
    }
  };

  // 폼 닫기
  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingUser(null);
  };

  // 필터 적용
  const handleApplyFilter = (newFilters: SearchFilters) => {
    console.log('필터 적용:', newFilters);
    setFilters(newFilters);
    // 필터에서 온 검색어도 반영
    if (newFilters.search !== undefined) {
      setSearchTerm(newFilters.search);
      setLocalSearchTerm(newFilters.search);
    }
    setPage(0);
  };

  // 필터 초기화
  const handleClearFilter = () => {
    setFilters({
      search: '',
      headquarters: '',
      department: '',
      position: '',
      assignments: '',
      location: '',
      initialEducationStartDate: '',
      initialEducationEndDate: '',
      recentEducationStartDate: '',
      recentEducationEndDate: ''
    });
    setSearchTerm('');
    setLocalSearchTerm(''); // 로컬 검색어도 초기화
    setPage(0);
  };

  // 사용 가능한 필터 옵션 계산
  const getFilterOptions = () => {
    const headquarters = Array.from(new Set(users.map(u => u.headquarters))).filter(Boolean).sort();
    const departments = Array.from(new Set(users.map(u => u.department))).filter(Boolean).sort();
    const positions = Array.from(new Set(users.map(u => u.position))).filter(Boolean).sort();
    const locations = Array.from(new Set(users.map(u => u.location))).filter(Boolean).sort();
    const assignments = Array.from(new Set(users.flatMap(u => u.assignments || []))).filter(Boolean).sort();

    return { headquarters, departments, positions, locations, assignments };
  };

  // 날짜 포맷팅
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '-';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'yyyy.MM.dd', { locale: ko });
    } catch {
      return '-';
    }
  };

  // 발령기간 포맷팅
  const formatAppointmentPeriod = (period: any): string => {
    if (!period?.start) return '-';
    
    const start = formatDate(period.start);
    const end = period.end ? formatDate(period.end) : '현재';
    
    return `${start} ~ ${end}`;
  };

  // 활성 필터 개수 계산
  const getActiveFilterCount = () => {
    return Object.values(filters).filter(value => value.trim() !== '').length + (searchTerm ? 1 : 0);
  };

  // 엑셀 다운로드 함수
  const handleExcelDownload = () => {
    try {
      // 엑셀용 데이터 준비
      const excelData = filteredUsers.map((user, index) => ({
        '순번': index + 1,
        '이름': user.name,
        '이메일': user.email,
        '사번': user.employeeId,
        '본부': user.headquarters,
        '부서': user.department,
        '직위': user.position,
        '직책': user.role,
        '발령 사항': user.assignments?.join(', ') || '-',
        '지점/공항': user.location || '-',
        '발령 시작일': user.appointmentPeriod?.start ? formatDate(user.appointmentPeriod.start) : '-',
        '발령 종료일': user.appointmentPeriod?.end ? formatDate(user.appointmentPeriod.end) : '현재',
        '비고': user.remarks || '',
        '등록일': formatDate(user.createdAt),
        '초기 보안교육': user.initialEducationDate ? formatDate(user.initialEducationDate) : '미이수',
        '정기 보안교육': user.recentEducationDate ? formatDate(user.recentEducationDate) : '미이수'
      }));

      // 워크북 생성
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      
      // 열 너비 설정
      worksheet['!cols'] = [
        { width: 8 },   // 순번
        { width: 12 },  // 이름
        { width: 25 },  // 이메일
        { width: 15 },  // 사번
        { width: 15 },  // 본부
        { width: 15 },  // 부서
        { width: 12 },  // 직위
        { width: 12 },  // 직책
        { width: 25 },  // 발령 사항
        { width: 15 },  // 지점/공항
        { width: 15 },  // 발령 시작일
        { width: 15 },  // 발령 종료일
        { width: 20 },  // 비고
        { width: 15 },  // 등록일
        { width: 15 },  // 초기 보안교육
        { width: 15 }   // 정기 보안교육
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, '인원 목록');
      
      // 파일명 생성 (현재 날짜 포함)
      const now = new Date();
      const fileName = `보안조직_인원목록_${format(now, 'yyyy-MM-dd_HH-mm-ss', { locale: ko })}.xlsx`;
      
      // 파일 다운로드
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('엑셀 다운로드 오류:', error);
      handleError(new Error('엑셀 파일 다운로드 중 오류가 발생했습니다.'), '엑셀 다운로드');
    }
  };

  // 서버에서 이미 필터링된 결과를 그대로 사용
  const filteredUsers = users;

  // 페이지네이션된 사용자 목록
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          인원 목록을 불러오는 중...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* 헤더 */}
      <Box display="flex" justifyContent="between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          인원 관리
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="새로고침">
            <IconButton onClick={handleRefresh} disabled={loading}>
              <Refresh />
            </IconButton>
          </Tooltip>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenForm()}
              sx={{ minWidth: 120 }}
            >
              인원 추가
            </Button>
          )}
        </Box>
      </Box>

      {/* 검색 및 필터 */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            placeholder="검색어를 입력하고 Enter키를 누르세요... (2글자 이상)"
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
            sx={{ flexGrow: 1 }}
            autoComplete="off"
            helperText={
              localSearchTerm.length === 1 ? "1글자 더 입력하고 Enter키를 누르세요" : 
              localSearchTerm.length >= 2 ? "Enter키를 누르면 검색됩니다" : 
              ""
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {localSearchTerm.length > 0 && (
                    <IconButton 
                      onClick={handleClearSearch}
                      size="small"
                      sx={{ mr: localSearchTerm.length >= 2 ? 0 : -1 }}
                      title="검색어 초기화"
                    >
                      <Clear color="action" />
                    </IconButton>
                  )}
                  {localSearchTerm.length >= 2 && (
                    <IconButton 
                      onClick={handleSearch}
                      size="small"
                      sx={{ mr: -1 }}
                      title="검색 실행"
                    >
                      <Search color="primary" />
                    </IconButton>
                  )}
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setFilterOpen(true)}
            color={getActiveFilterCount() > 0 ? 'primary' : 'inherit'}
          >
            필터 {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExcelDownload}
            disabled={filteredUsers.length === 0}
          >
            내보내기
          </Button>
        </Box>
      </Card>

      {/* 통계 정보 */}
      <Box display="flex" gap={2} mb={3}>
        <Chip 
          label={`전체 ${filteredUsers.length}명`} 
          color="primary" 
          variant="outlined" 
        />
        {searchTerm && (
          <Chip 
            label={`검색 결과 ${filteredUsers.length}명`} 
            color="secondary" 
          />
        )}
      </Box>

      {/* 테이블 */}
      <Card>
        <TableContainer sx={{ 
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c1c1c1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#a8a8a8',
          }
        }}>
          <Table size="small" sx={{ 
            minWidth: 1200,
            '& .MuiTableCell-root': { 
              padding: '8px 4px',
              fontSize: '0.75rem',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '40px', minWidth: '40px' }}>프로필</TableCell>
                    <TableCell sx={{ width: '80px', minWidth: '80px' }}>이름</TableCell>
                    <TableCell sx={{ width: '140px', minWidth: '140px' }}>이메일</TableCell>
                    <TableCell sx={{ width: '60px', minWidth: '60px' }}>사번</TableCell>
                    <TableCell sx={{ width: '70px', minWidth: '70px' }}>본부</TableCell>
                    <TableCell sx={{ width: '70px', minWidth: '70px' }}>부서</TableCell>
                    <TableCell sx={{ width: '60px', minWidth: '60px' }}>직위</TableCell>
                    <TableCell sx={{ width: '70px', minWidth: '70px' }}>직책</TableCell>
                    <TableCell sx={{ width: '120px', minWidth: '120px' }}>발령 사항</TableCell>
                    <TableCell sx={{ width: '80px', minWidth: '80px' }}>지점/공항</TableCell>
                    <TableCell sx={{ width: '100px', minWidth: '100px' }}>발령기간</TableCell>
                    <TableCell sx={{ width: '90px', minWidth: '90px' }}>초기 보안교육</TableCell>
                    <TableCell sx={{ width: '90px', minWidth: '90px' }}>정기 보안교육</TableCell>
                    <TableCell sx={{ width: '80px', minWidth: '80px' }}>등록일</TableCell>
                    {canEdit && <TableCell align="center" sx={{ width: '60px', minWidth: '60px' }}>작업</TableCell>}
                  </TableRow>
                </TableHead>
            <TableBody>
              {paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 15 : 14} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      {searchTerm ? '검색 결과가 없습니다.' : '등록된 인원이 없습니다.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <UserAvatar
                        photoUrl={user.photo?.url}
                        name={user.name}
                        size={40}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>
                      {user.name}
                    </TableCell>
                    <TableCell sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      letterSpacing: '-0.5px'
                    }}>
                      <span title={user.email}>{user.email}</span>
                    </TableCell>
                    <TableCell>{user.employeeId}</TableCell>
                    <TableCell>{user.headquarters}</TableCell>
                    <TableCell>{user.department}</TableCell>
                    <TableCell>{user.position}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell sx={{ whiteSpace: 'normal' }}>
                      {user.assignments && user.assignments.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.3 }}>
                          {user.assignments.map((assignment, index) => (
                            <Chip
                              key={index}
                              label={assignment}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{
                                fontSize: '0.65rem',
                                height: '20px',
                                '& .MuiChip-label': {
                                  padding: '0 4px',
                                  letterSpacing: '-0.3px'
                                }
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <span style={{ color: '#999' }}>-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.location || '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>
                      {formatAppointmentPeriod(user.appointmentPeriod)}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // 파일이 업로드되어 있으면 "이수", 없으면 "미이수"
                        const hasInitialCert = user.initialEducationCert && 
                                             user.initialEducationCert.url && 
                                             user.initialEducationCert.fileName;
                        
                        console.log(`${user.name} 초기교육 파일 확인:`, {
                          hasFile: hasInitialCert,
                          certData: user.initialEducationCert
                        });
                        
                        return hasInitialCert ? (
                          <Box>
                            <Chip 
                              label="이수" 
                              color="success" 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: '18px' }}
                            />
                            <div style={{ fontSize: '0.6rem', color: '#666', marginTop: '2px' }}>
                              파일 업로드됨
                            </div>
                          </Box>
                        ) : (
                          <Chip 
                            label="미이수" 
                            color="error" 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: '18px' }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // 파일이 업로드되어 있으면 "이수", 없으면 "미이수"
                        const hasRecentCert = user.recentEducationCert && 
                                            user.recentEducationCert.url && 
                                            user.recentEducationCert.fileName;
                        
                        console.log(`${user.name} 정기교육 파일 확인:`, {
                          hasFile: hasRecentCert,
                          certData: user.recentEducationCert
                        });
                        
                        return hasRecentCert ? (
                          <Box>
                            <Chip 
                              label="이수" 
                              color="success" 
                              size="small" 
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: '18px' }}
                            />
                            <div style={{ fontSize: '0.6rem', color: '#666', marginTop: '2px' }}>
                              파일 업로드됨
                            </div>
                          </Box>
                        ) : (
                          <Chip 
                            label="미이수" 
                            color="error" 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: '18px' }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                      {formatDate(user.createdAt)}
                    </TableCell>
                    {canEdit && (
                      <TableCell align="center">
                        <Box display="flex" gap={0.5}>
                          <Tooltip title="편집">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenForm(user)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {isAdmin && (
                            <Tooltip title="삭제">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(user)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* 페이지네이션 */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="페이지당 행 수:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} / ${count !== -1 ? count : `${to}개 이상`}`
          }
        />
      </Card>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>인원 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{userToDelete?.name}</strong>님의 정보를 삭제하시겠습니까?
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            삭제된 데이터는 복구할 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            취소
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : null}
          >
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 플로팅 액션 버튼 (모바일용) */}
      {canEdit && (
        <Fab
          color="primary"
          aria-label="add user"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' }
          }}
          onClick={() => handleOpenForm()}
        >
          <Add />
        </Fab>
      )}

      {/* 인원 추가/편집 폼 */}
      <UserForm
        open={formOpen}
        onClose={handleCloseForm}
        onSuccess={handleFormSuccess}
        editUser={editingUser}
      />

      {/* 필터 다이얼로그 */}
      <UserFilter
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyFilter}
        onClear={handleClearFilter}
        currentFilters={{...filters, search: searchTerm}}
        availableOptions={getFilterOptions()}
      />
    </Box>
  );
};

export default UserList;
